import { Button, Input, Switch } from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ContentLoadError, PageTitle, Spinner } from '../../../components';
import { useHeroToast } from '../../../hooks';
import {
  HOTEL_STRIPE_FEES_QUERY_KEY,
  OUTLET_KIND_LABELS,
  OUTLET_KINDS,
  OutletKind,
  useGetHotelStripeFees,
  useGetOrganization,
  useSetHotelStripeFee,
  useSetStripeAvailability,
} from '../../../queries';

/**
 * Prowizja HotelSpot od sprzedaży w punktach hotelu.
 *
 * Ekran jest w hs-adminie, a nie w panelu hotelu, bo stawka wynika z UMOWY — hotel widzi
 * ją u siebie tylko do odczytu. Zapis idzie przez `v3-setHotelStripeFee` za
 * `verifyAdminAccess`; ukrycie ekranu nie jest tu zabezpieczeniem, tylko wygodą.
 *
 * DWA POZIOMY: stawka domyślna hotelu i stawki per typ punktu. Puste pole typu znaczy
 * „dziedzicz", a wpisane `0` — „ten typ nie płaci prowizji mimo umowy". To rozróżnienie
 * jest celowe i widoczne w interfejsie, bo w danych też jest realne.
 */

type FeeInputs = Record<OutletKind, string>;

/**
 * Wyprowadzone z `OUTLET_KINDS`, a NIE wypisane ręcznie: ręczna lista rozjechała się
 * z dodaniem serwisu śniadaniowego i widok wywalał się na `byKind[kind].trim()` jeszcze
 * zanim cokolwiek się wyrenderowało. Każdy kolejny typ punktu dochodzi tu sam.
 */
const EMPTY_INPUTS: FeeInputs = OUTLET_KINDS.reduce(
  (acc, kind) => ({ ...acc, [kind]: '' }),
  {} as FeeInputs,
);

const isValidFee = (value: string): boolean => {
  if (value.trim() === '') return true; // puste = dziedziczy
  const parsed = Number(value.replace(',', '.'));
  return Number.isFinite(parsed) && parsed >= 0 && parsed <= 100;
};

const toNumber = (value: string): number => Number(value.replace(',', '.'));

export const StripeFees = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useHeroToast();

  const { data } = useGetOrganization(id || '');
  const organization = data?.organization;
  const { data: fees, isLoading, error } = useGetHotelStripeFees(id);
  const { mutateAsync: saveFee, isPending: isSaving } = useSetHotelStripeFee();
  const { mutateAsync: setAvailability, isPending: isTogglingAvailability } = useSetStripeAvailability();

  const [defaultFee, setDefaultFee] = useState('');
  const [byKind, setByKind] = useState<FeeInputs>(EMPTY_INPUTS);

  useEffect(() => {
    if (!fees) return;
    setDefaultFee(String(fees.feePercent ?? 0));
    setByKind(OUTLET_KINDS.reduce((acc, kind) => ({
      ...acc,
      [kind]: fees.feeByKind?.[kind] === undefined ? '' : String(fees.feeByKind[kind]),
    }), EMPTY_INPUTS));
  }, [fees]);

  /** Co realnie trzeba wysłać — tylko pola zmienione względem stanu z serwera. */
  const changes = useMemo(() => {
    if (!fees) return [];
    const list: Array<{ kind?: OutletKind; feePercent: number | null }> = [];

    if (defaultFee.trim() !== '' && toNumber(defaultFee) !== (fees.feePercent ?? 0)) {
      list.push({ feePercent: toNumber(defaultFee) });
    }

    OUTLET_KINDS.forEach((kind) => {
      const saved = fees.feeByKind?.[kind];
      const current = (byKind[kind] ?? '').trim();
      if (current === '' && saved !== undefined) list.push({ kind, feePercent: null });
      if (current !== '' && toNumber(current) !== saved) list.push({ kind, feePercent: toNumber(current) });
    });

    return list;
  }, [fees, defaultFee, byKind]);

  const hasInvalid = !isValidFee(defaultFee) || OUTLET_KINDS.some((kind) => !isValidFee(byKind[kind] ?? ''));

  const handleSave = async (): Promise<void> => {
    if (!id || hasInvalid || changes.length === 0) return;
    try {
      // Po kolei, nie równolegle: każde żądanie scala się z tym samym dokumentem,
      // a przy równoległym zapisie ostatnia odpowiedź potrafi nadpisać wcześniejsze.
      // eslint-disable-next-line no-restricted-syntax
      for (const change of changes) {
        // eslint-disable-next-line no-await-in-loop
        await saveFee({ hotelId: id, ...change });
      }
      await queryClient.invalidateQueries({ queryKey: [HOTEL_STRIPE_FEES_QUERY_KEY, id] });
      showSuccess('Stawki prowizji zapisane');
    } catch {
      showError('Nie udało się zapisać stawek');
    }
  };

  /**
   * Wyłącznik dostępu po naszej stronie. Backend odmówi odebrania, dopóki jakiś punkt
   * korzysta z konta Stripe: hotel straciłby wtedy ekran, z którego to odpina.
   */
  const handleAvailability = async (available: boolean): Promise<void> => {
    if (!id) return;
    try {
      await setAvailability({ hotelId: id, available });
      await queryClient.invalidateQueries({ queryKey: [HOTEL_STRIPE_FEES_QUERY_KEY, id] });
      showSuccess(available ? 'Dostęp do płatności przywrócony' : 'Dostęp do płatności odebrany');
    } catch {
      showError('Nie udało się zmienić dostępności. Obiekt może mieć jeszcze wpięte punkty.');
    }
  };

  if (isLoading) return <Spinner />;
  if (error) return <ContentLoadError />;

  return (
    <div className="max-w-[720px]">
      <PageTitle title={`Prowizje Stripe — ${organization?.hotel || organization?.name || id}`} />

      <p className="text-sm text-default-500 mt-2 mb-6">
        Prowizja pobierana od każdej płatności gościa jako
        {' '}
        <code>application_fee</code>
        . Stawka typu punktu ma pierwszeństwo nad domyślną; puste pole oznacza dziedziczenie,
        a wpisane 0 — że ten typ nie płaci prowizji.
      </p>

      <div className="rounded-lg border border-default-200 p-4 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-medium">Płatności online dostępne dla obiektu</p>
            <p className="text-sm text-default-500 mt-1">
              Domyślnie włączone: hotel sam uruchamia Stripe w swoich ustawieniach, akceptując regulamin.
              Wyłącz tylko wtedy, gdy chcesz odebrać dostęp (spór, zaległości, obiekt spoza oferty).
              Wtedy kafelek integracji znika hotelowi z panelu.
            </p>
          </div>
          <Switch
            isSelected={fees?.paymentsAvailable === true}
            isDisabled={isTogglingAvailability}
            onValueChange={handleAvailability}
            aria-label="Płatności online dostępne dla obiektu"
          />
        </div>

        <div className="text-sm text-default-500 mt-3">
          {'Stan po stronie obiektu: '}
          <span className={fees?.paymentsEnabled ? 'text-success' : 'text-default-600'}>
            {fees?.paymentsEnabled ? 'włączone' : 'niewłączone'}
          </span>
          {fees?.termsAcceptedBy && (
            <>
              {`. Regulamin zaakceptował(a) ${fees.termsAcceptedBy}`}
              {fees.termsAcceptedAt ? `, ${new Date(fees.termsAcceptedAt).toLocaleString()}` : ''}
              {fees.termsVersion ? ` (wersja ${fees.termsVersion})` : ''}
            </>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <Input
          type="text"
          label="Stawka domyślna hotelu"
          description="Obowiązuje typy punktów bez własnej stawki."
          value={defaultFee}
          onValueChange={setDefaultFee}
          endContent={<span className="text-default-400 text-sm">%</span>}
          isInvalid={!isValidFee(defaultFee)}
          errorMessage="Podaj liczbę z zakresu 0–100"
          className="max-w-[320px]"
        />

        <div className="h-px bg-default-200 my-2" />

        {OUTLET_KINDS.map((kind) => (
          <Input
            key={kind}
            type="text"
            label={OUTLET_KIND_LABELS[kind]}
            placeholder={`dziedziczy (${defaultFee || 0}%)`}
            value={byKind[kind] ?? ''}
            onValueChange={(value) => setByKind((prev) => ({ ...prev, [kind]: value }))}
            endContent={<span className="text-default-400 text-sm">%</span>}
            isInvalid={!isValidFee(byKind[kind] ?? '')}
            errorMessage="Podaj liczbę z zakresu 0–100"
            className="max-w-[320px]"
          />
        ))}
      </div>

      <div className="flex items-center gap-3 mt-8">
        <Button color="primary" onPress={handleSave} isDisabled={hasInvalid || changes.length === 0 || isSaving}>
          {isSaving ? 'Zapisywanie...' : `Zapisz${changes.length ? ` (${changes.length})` : ''}`}
        </Button>
        <Button variant="light" startContent={<ArrowLeft size={14} />} onPress={() => navigate('/organizations')}>
          Wróć do listy
        </Button>
      </div>
    </div>
  );
};
